/*
 * Copyright (c) 2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import App from 'apmui/App';
import EventTags from 'apmui/EventTags';
import IPageModel from 'apmui/page';
import h from 'apmui/util/createElement';
import { loadScript, loadStyle } from 'apmui/util/loader';
import CustomEvent from 'apmui/util/CustomEvent';

/**
 * Resolve and load application modules
 */
class AppLoader {
    /**
     * Id of HTML Element to be used as rendering target for the application.
     * If the element does not exist, it will be created and attached to document.body.
     */
    static readonly CONTAINER_ID = 'apmui-container';
    /**
     * Default class to assign to created container element.
     */
    static readonly DEFAULT_CONTAINER_CLASS = AppLoader.CONTAINER_ID;

    static get(): App {
        return App.get();
    }

    private app = App.get();
    private root?: Element;
    private view = {
        render(store: IPageModel) {
            throw new Error('Renderer is not ready');
        },
    };

    constructor() {
        this.app.defaultController = (event: Event, data: IPageModel) => {
            /**
             * Do not render on initial configuration event.
             * User code should not be able to use CONFIGURE event, thus we need to wait
             * until all customizations on START would finish applying UI changes.
             */
            if (event.type === EventTags.CONFIGURE) {
                return;
            }
            this.view.render(data);
        };
    }

    /**
     * Find or create container HTML element if none is given to Application
     */
    getApplicationContainer(): Element {
        if (!this.root) {
            let elem = document.getElementById(AppLoader.CONTAINER_ID);
            if (!elem) {
                elem = h('div', {
                    id: AppLoader.CONTAINER_ID,
                    className: AppLoader.DEFAULT_CONTAINER_CLASS,
                });
                document.body.appendChild(elem);
            }
            this.root = elem;
        }
        return this.root;
    }

    /**
     * Set page configuration.
     *
     * @param config page configuration object
     */
    configure(config: Partial<IPageModel>) {
        this.app.configure(config);
    }

    /**
     * Initialize application.
     * This method will load all controller modules and dispatch
     * START event to all subscribers.
     */
    run() {
        const data = this.app.data;
        const asyncLoadQueue: Array<Promise<unknown>> = [];

        const errorHandler = (err: any) => {
            console.error(err);
            return err;
        };
        const registrationHandler = (x: any) => x;

        /* Common timeout handler */
        if (data.general && data.general.controllers && data.general.controllers.policyTimeout !== false) {
            asyncLoadQueue.push(import('apmui/common/SessionTimeoutController'));
        }

        /* Common page initialization */
        asyncLoadQueue.push(import('apmui/master/Controller'));

        /* Page-specific code */
        asyncLoadQueue.push(import('apmui/page/' + data.pageType + '/Controller'));

        /* Apply styles from customization data */
        if (Array.isArray(data.styles)) {
            asyncLoadQueue.push.apply(asyncLoadQueue, data.styles.map(loadStyle));
        }

        /* Add scripts from customization data */
        if (Array.isArray(data.scripts)) {
            asyncLoadQueue.push.apply(asyncLoadQueue, data.scripts.map(loadScript));
        }

        /*
         * Run the tasks and process results in order.
         */
        asyncLoadQueue
            /*
             * Error handler has to be defined synchronously before Promise resolution
             * because otherwise Chrome debugger considers rejection unhandled.
             */
            .map((x) => x.catch(errorHandler))
            .reduce(
                (result, next) =>
                    result
                        /* Wait for the result */
                        .then(() => next)
                        /* Register the result if it's a module */
                        .then(registrationHandler),
                Promise.resolve(null)
            )
            /**
             * Defer resolution of View until every bit of customization code is loaded.
             * This would allow administrators to redefine 'apmui/master/View' with their custom UI code.
             */
            .then(() =>
                import('apmui/master/View').then((ViewModule: any) => {
                    this.view = new (ViewModule.default || ViewModule)(this.getApplicationContainer());
                })
            )
            .then(() => {
                /* Allow modules to pre-process and validate configuration */
                this.app.dispatch(new CustomEvent(EventTags.CONFIGURE, { cancelable: false }));
                /* Everything is loaded and ready; start application */
                this.app.dispatch(new CustomEvent(EventTags.START, { cancelable: false }));
            });
    }
}

export default AppLoader;
